import { z } from 'zod';

import React, { createContext, useContext, useEffect, useState } from 'react';

import {
  fetchPurchaseJourneyContent,
  getPurchaseJourneyContentEndpoint,
} from './purchase-journey-content-api';

const legalLinkKeySchema = z.enum([
  'consumerTerms',
  'privacyPolicy',
  'directDebitForm',
  'keyThingsToKnowTv',
]);

const notificationImageKeySchema = z.enum([
  'emptyCart',
  'addressOrFibreAvailable',
  'addressOrFibreNotAvailable',
  'congratsOffer',
  'trash',
  'upgrade',
]);

const notificationIconKeySchema = z.enum(['PHONE_AB']);

const contentLinkSchema = z.object({
  id: z.string().min(1),
  label1: z.string().min(1),
  linkLabel1: z.string().min(1).optional(),
  linkKey1: legalLinkKeySchema.optional(),
  label2: z.string().min(1).optional(),
  linkLabel2: z.string().min(1).optional(),
  linkKey2: legalLinkKeySchema.optional(),
});

const notificationButtonSchema = z.object({
  btnText: z.string().min(1).optional(),
  variant: z.string().min(1),
  iconKey: notificationIconKeySchema.optional(),
});

const notificationModalSchema = z.object({
  title: z.string().min(1),
  titleTwo: z.string().optional(),
  message: z.string(),
  imageKey: notificationImageKeySchema.nullable(),
  btnItem: z.array(notificationButtonSchema),
});

const legacySwitchButtonSchema = z.object({
  label: z.string().min(1),
});

const legacyTitleOptionSchema = z.object({
  title: z.string().min(1),
});

const legacyFallbackItemSchema = z.object({
  title: z.string().min(1),
  label: z.string().min(1),
  price: z.number(),
});

const richTextNodeSchema: z.ZodType<{
  nodeType: string;
  data: Record<string, unknown>;
  content?: Array<any>;
  value?: string;
  marks?: Array<{ type: string }>;
}> = z.lazy(() =>
  z.object({
    nodeType: z.string().min(1),
    data: z.record(z.unknown()),
    content: z.array(richTextNodeSchema).optional(),
    value: z.string().optional(),
    marks: z.array(z.object({ type: z.string().min(1) })).optional(),
  })
);

const richTextDocumentSchema = richTextNodeSchema.refine(node => node.nodeType === 'document', {
  message: 'Rich text root node must be a document',
});

const purchaseJourneyContentSchema = z.object({
  version: z.literal(1),
  checkout: z.object({
    navigation: z.object({
      plan: z.string().min(1),
      aboutYou: z.string().min(1),
      checkout: z.string().min(1),
      confirmation: z.string().min(1),
    }),
    cartSummary: z.object({
      continueCta: z.string().min(1),
      placeOrderCta: z.string().min(1),
      priceSummaryTitle: z.string().min(1),
      monthlyChargesTitle: z.string().min(1),
    }),
    priceIncreaseCallout: z
      .object({
        title: z.string().min(1),
        description: z.string().min(1),
        eReceiptDescription: z.string().min(1).optional(),
      })
      .optional(),
    saveCart: z.object({
      title: z.string().min(1),
      subtitle: z.string().min(1),
      submitCta: z.string().min(1),
      namePlaceholder: z.string().min(1),
      nameErrorLabel: z.string().min(1),
      emailPlaceholder: z.string().min(1),
      emailErrorLabel: z.string().min(1),
      validation: z.object({
        firstNameRequired: z.string().min(1),
        firstNameMinLength: z.string().min(1),
        emailRequired: z.string().min(1),
        emailMaxLength: z.string().min(1),
        emailInvalid: z.string().min(1),
      }),
    }),
    broadbandInstallation: z.object({
      mobileCommunicationHeading: z.string().min(1),
      phonePlaceholder: z.string().min(1),
      phoneErrorLabel: z.string().min(1),
      changingProviderHeading: z.string().min(1),
      changingProviderDescription: z.string().min(1),
      providerPlaceholder: z.string().min(1),
      providerSelectorTitle: z.string().min(1),
      providerModalCloseAriaLabel: z.string().min(1),
      providerAccountNumberPlaceholder: z.string().min(1),
      providerAccountNumberErrorLabel: z.string().min(1),
      validation: z.object({
        phoneRequired: z.string().min(1),
        mobileInvalidPrefix: z.string().min(1),
        providerConsentRequired: z.string().min(1),
        currentProviderRequired: z.string().min(1),
        providerAccountNumberRequired: z.string().min(1),
      }),
    }),
    homePhone: z.object({
      heading: z.string().min(1),
      existingPhoneQuestion: z.string().min(1),
      keepCurrentPhoneQuestion: z.string().min(1),
      providerMatchQuestion: z.string().min(1),
      provideNumberHeading: z.string().min(1),
      homeNumberPlaceholder: z.string().min(1),
      homeNumberErrorLabel: z.string().min(1),
      currentProviderPlaceholder: z.string().min(1),
      otherProviderPlaceholder: z.string().min(1),
      otherProviderErrorLabel: z.string().min(1),
      accountNumberHelperText: z.string().min(1),
      accountNumberPlaceholder: z.string().min(1),
      accountNumberErrorLabel: z.string().min(1),
      numberSelectionHelperText: z.string().min(1),
      providerSelectorTitle: z.string().min(1),
      validation: z.object({
        selectionRequired: z.string().min(1),
        homeNumberRequired: z.string().min(1),
        currentProviderRequired: z.string().min(1),
        otherProviderRequired: z.string().min(1),
        otherProviderMinLength: z.string().min(1),
        accountNumberRequired: z.string().min(1),
        accountNumberDigitsOnly: z.string().min(1),
        accountNumberNotAllZeros: z.string().min(1),
        accountNumberMinLength: z.string().min(1),
        accountNumberMaxLength: z.string().min(1),
        newNumberRequired: z.string().min(1),
      }),
    }),
    confirmation: z.object({
      verification: z.object({
        resendCta: z.string().min(1),
        orderSuccessTitle: z.string().min(1),
        welcomeMessage: z.string().min(1),
        accountNumberLabel: z.string().min(1),
        orderFailed: z.string().min(1),
        resendPrompt: z.string().min(1),
        resendCountdown: z.string().min(1),
        resendAvailable: z.string().min(1),
        resendError: z.string().min(1),
      }),
      purchaseFailedTitle: z.string().min(1),
    }),
    installationDetails: z.object({
      infoButtonAriaLabel: z.string().min(1),
      addressRequiredMessage: z.string().min(1),
      noAvailabilityMessage: z.string().min(1),
      schedule: z.object({
        heading: z.string().min(1),
        dateLabel: z.string().min(1),
        datePlaceholder: z.string().min(1),
        datePickerPlaceholder: z.string().min(1),
        timePlaceholder: z.string().min(1),
        emptyDateMessage: z.string().min(1),
        allDayMessagePrefix: z.string().min(1),
        allDayTimeWindow: z.string().min(1),
        allDayMessageSuffix: z.string().min(1),
        selectedTimeMessage: z.string().min(1),
      }),
      attendance: z.object({
        question: z.string().min(1),
        youLabel: z.string().min(1),
        someoneElseLabel: z.string().min(1),
      }),
      dogsAtHome: z.object({
        question: z.string().min(1),
        yesLabel: z.string().min(1),
        noLabel: z.string().min(1),
      }),
      alternateContact: z.object({
        heading: z.string().min(1),
        helperText: z.string().min(1),
        namePlaceholder: z.string().min(1),
        nameErrorLabel: z.string().min(1),
        phonePlaceholder: z.string().min(1),
        phoneErrorLabel: z.string().min(1),
        validation: z.object({
          nameRequired: z.string().min(1),
          nameMinLength: z.string().min(1),
          nameMaxLength: z.string().min(1),
          invalidCharacters: z.string().min(1),
          phoneRequired: z.string().min(1),
        }),
      }),
      validation: z.object({
        installationTimeRequired: z.string().min(1),
        installationDateRequired: z.string().min(1),
        selectionRequired: z.string().min(1),
      }),
      infoDialog: z.object({
        title: z.string().min(1),
        description: z.string().min(1),
        includedMessage: z.string().min(1),
        closeButtonAriaLabel: z.string().min(1),
      }),
    }),
    hardwareDelivery: z.object({
      heading: z.string().min(1),
      addressRequiredMessage: z.string().min(1),
      useDifferentAddressQuestion: z.string().min(1),
      deliveryAddress: z.object({
        label: z.string().min(1),
        changeAddressCta: z.string().min(1),
        placeholder: z.string().min(1),
        dialog: z.object({
          heading: z.string().min(1),
          description: z.string().min(1),
          closeButtonAriaLabel: z.string().min(1),
        }),
      }),
      broadbandDeliveryAddress: z.object({
        placeholder: z.string().min(1),
      }),
      validation: z.object({
        selectionRequired: z.string().min(1),
        addressRequired: z.string().min(1),
        addressInvalid: z.string().min(1),
        addressSuggestionRequired: z.string().min(1),
      }),
    }),
    aboutYou: z.object({
      serviceAddress: z.object({
        placeholder: z.string().min(1),
        validation: z.object({
          required: z.string().min(1),
          invalid: z.string().min(1),
        }),
      }),
      landlordPermissionMessage: z.string().min(1),
      additionalNotes: z.object({
        label: z.string().min(1),
        placeholder: z.string().min(1),
        validation: z.object({
          maxLength: z.string().min(1),
        }),
      }),
      deliveryInstructions: z.object({
        label: z.string().min(1),
        placeholder: z.string().min(1),
        validation: z.object({
          maxLength: z.string().min(1),
        }),
      }),
      personalDetails: z.object({
        heading: z.string().min(1),
        firstName: z.object({
          placeholder: z.string().min(1),
          errorLabel: z.string().min(1),
          validation: z.object({
            required: z.string().min(1),
            minLength: z.string().min(1),
            invalidCharacters: z.string().min(1),
          }),
        }),
        lastName: z.object({
          placeholder: z.string().min(1),
          errorLabel: z.string().min(1),
          validation: z.object({
            required: z.string().min(1),
            minLength: z.string().min(1),
            invalidCharacters: z.string().min(1),
          }),
        }),
        email: z.object({
          placeholder: z.string().min(1),
          errorLabel: z.string().min(1),
          validation: z.object({
            required: z.string().min(1),
            maxLength: z.string().min(1),
            invalid: z.string().min(1),
          }),
        }),
        dateOfBirth: z.object({
          emptyLabel: z.string().min(1),
          validation: z.object({
            required: z.string().min(1),
            invalid: z.string().min(1),
            invalidDate: z.string().min(1),
            minimumAge: z.string().min(1),
          }),
        }),
        phoneNumber: z.object({
          placeholder: z.string().min(1),
          errorLabel: z.string().min(1),
          mobilePlaceholder: z.string().min(1).optional(),
          mobileErrorLabel: z.string().min(1).optional(),
          validation: z.object({
            required: z.string().min(1),
            mobileRequired: z.string().min(1).optional(),
          }),
        }),
      }),
    }),
    payment: z.object({
      heading: z.string().min(1),
      options: z.object({
        creditCard: z.string().min(1),
        directDebit: z.string().min(1),
      }),
      validation: z.object({
        paymentMethodRequired: z.string().min(1),
        paymentOptionRequired: z.string().min(1),
      }),
      loading: z.object({
        directDebit: z.string().min(1),
        creditCard: z.string().min(1),
      }),
      errors: z.object({
        creditCardPaymentFailed: z.string().min(1),
        orderCreationFailed: z.string().min(1),
        paymentFailed: z.string().min(1),
      }),
      directDebit: z.object({
        heading: z.string().min(1),
        bankNamePlaceholder: z.string().min(1),
        accountNumberPlaceholder: z.string().min(1),
        validation: z.object({
          bankNameRequired: z.string().min(1),
        }),
      }),
      bankSearch: z.object({
        heading: z.string().min(1),
        placeholder: z.string().min(1),
      }),
      creditCard: z.object({
        heading: z.string().min(1),
        cardNumberPlaceholder: z.string().min(1),
        fullNamePlaceholder: z.string().min(1),
        expiryDatePlaceholder: z.string().min(1),
        cvvPlaceholder: z.string().min(1),
      }),
    }),
    voucher: z.object({
      heading: z.string().min(1),
      prompt: z.string().min(1),
      codePlaceholder: z.string().min(1),
      applyCta: z.string().min(1),
      invalidCodeError: z.string().min(1),
    }),
  }),
  productSelection: z.object({
    validation: z.object({
      deviceRequired: z.string().min(1),
    }),
    cta: z.object({
      add: z.string().min(1),
      added: z.string().min(1),
    }),
    addons: z.object({
      heading: z.string().min(1),
      subheading: z.string().min(1),
      moreSectionText: z.string().min(1),
    }),
    homePhoneAddons: z.object({
      heading: z.string().min(1),
      subheading: z.string().min(1),
    }),
  }),
  discovery: z.object({
    addressChecker: z.object({
      successPrefix: z.string().min(1),
      fibreUnavailableMessage: z.string().min(1),
      notYourAddressLabel: z.string().min(1),
      addressPlaceholder: z.string().min(1),
    }),
  }),
  purchase: z.object({
    modalInfo: z.object({
      SKY_HOME_PHONE_NUMBER: z.object({
        thumbnail: z.string().min(1),
        title: z.string().optional(),
        moreInfoList: z.array(
          z.object({
            imageKey: z.string().optional(),
            title: z.string().min(1),
            description: z.string().min(1),
          })
        ),
      }),
      SKY_HOME_PHONE_PROVIDER: z.object({
        thumbnail: z.string().min(1),
        title: z.string().optional(),
        moreInfoList: z.array(
          z.object({
            imageKey: z.string().optional(),
            title: z.string().min(1),
            description: z.string().min(1),
          })
        ),
      }),
      ESTIMATE_BILL_INFO: z
        .object({
          thumbnail: z.string().optional(),
          title: z.string().optional(),
          moreInfoList: z.array(
            z.object({
              imageKey: z.string().optional(),
              title: z.string().min(1),
              description: z.string().min(1),
            })
          ),
        })
        .optional(),
    }),
  }),
  notifications: z.object({
    modalInfo: z.object({
      EMPTY_CART: notificationModalSchema,
      FIBRE_AVAILABLE: notificationModalSchema,
      FIBRE_NOT_AVAILABLE: notificationModalSchema,
      ADDRESS_NOT_FOUND: notificationModalSchema,
      CONGRATS_OFFERS_PRODUCT: notificationModalSchema,
      REMOVE_OFFER: notificationModalSchema,
      REMOVE_PLAN: notificationModalSchema,
      FIBRE_NOT_AVAILABLE_WITHOUT_BROADBAND: notificationModalSchema,
      PLAN_UPGRADE: notificationModalSchema,
      ADDRESS_CHECKER_FOR_BOARDBAND: notificationModalSchema,
      DELETE_PLAN: notificationModalSchema,
      SKY_ROUTER_ALERT: notificationModalSchema,
      BLOCKED_USER: notificationModalSchema,
      BANK_NOT_FOUND: notificationModalSchema,
      PROMO_VOUCHER_INFO: notificationModalSchema,
      LOGIN_USER_VERIFY: notificationModalSchema,
      EXISTING_USER_VERIFY: notificationModalSchema,
    }),
  }),
  legalContent: z.object({
    addressNotEmptyMessage: z.string().min(1),
    checkAnotherAddressCta: z.string().min(1),
    fibreAvailableMessage: z.string().min(1),
    links: z.object({
      directDebitForm: z.string().min(1),
      keyThingsToKnowTv: z.string().min(1),
    }),
    errors: z.object({
      unknownLinkKey: z.string().min(1),
    }),
    directDebitAuthority: z.array(contentLinkSchema),
    checkoutConsentLabels: z.array(contentLinkSchema),
    creditCheckMessage: contentLinkSchema.omit({ id: true }),
    saveCartConsentLabels: z.array(
      z.object({
        id: z.string().min(1),
        label1: z.string().min(1),
        linkLabel1: z.string().min(1).optional(),
        linkKey1: legalLinkKeySchema.optional(),
      })
    ),
    serviceProviderForBroadband: z.string().min(1),
  }),
  legacyConstants: z.object({
    addressLabels: z.object({
      serviceAddress: z.string().min(1),
      hardwareDeliveryAddress: z.string().min(1),
    }),
    legalSpeedDisclaimer: richTextDocumentSchema,
    stepPointer: z.array(
      z.object({
        sectionIndex: z.number(),
        title: z.string().min(1),
        infoTitle: z.string().min(1),
        sectionName: z.string().min(1),
        isInfo: z.boolean(),
      })
    ),
    estimateBillLabels: z.object({
      oneMonthInAdvance: z.string().min(1),
      firstBillEstimate: z.string().min(1),
      firstMonth: z.string().min(1),
      title: z.string().min(1).optional(),
      estimateBillBtnText: z.string().min(1).optional(),
    }),
    switchButtons: z.object({
      broadband: z.array(legacySwitchButtonSchema),
      homePhone: z.array(legacySwitchButtonSchema),
      installation: z.array(legacySwitchButtonSchema),
      dogOptions: z.array(legacyTitleOptionSchema),
      address: z.array(legacySwitchButtonSchema),
      hardwareDelivery: z.array(legacySwitchButtonSchema),
      vulnerable: z.array(legacySwitchButtonSchema),
    }),
    selectedDialogInfo: z.object({
      title: z.string().min(1),
      subTitle: z.string().min(1),
      options: z.array(
        z.object({
          title: z.string().min(1),
          isCheck: z.boolean(),
        })
      ),
      btnText: z.string().min(1),
    }),
    fallbackItems: z.object({
      deviceNotSelected: legacyFallbackItemSchema,
      addonsNotSelected: legacyFallbackItemSchema,
      productLabels: z.array(
        z.object({
          Titleline: z.string().min(1),
          textLine: z.string().min(1),
        })
      ),
    }),
    paymentCheckBoxLabels: z.object({
      authLabel: z.string().min(1),
      checkBoxLabels: z.array(z.string().min(1)),
    }),
    contextGAMessageForBlockCustomer: z.record(z.string().min(1)),
  }),
  sharedValidation: z.object({
    errors: z.object({
      generic: z.string().min(1),
    }),
    phone: z.object({
      required: z.string().min(1),
      minDigits: z.string().min(1),
      prefix: z.string().min(1),
      landlineLength: z.string().min(1),
      mobileLength: z.string().min(1),
      mobilePrefix: z.string().min(1),
      mobileOnly: z.string().min(1),
      allZeros: z.string().min(1),
      landlineOnly: z.string().min(1),
      broadbandMobileRequired: z.string().min(1).optional(),
    }),
    address: z.object({
      required: z.string().min(1),
      fibreRequired: z.string().min(1),
      minimumCharacters: z.string().min(1),
      selectFromDropdown: z.string().min(1),
    }),
  }),
  bundlePlanSelection: z
    .object({
      offerCode: z.record(
        z.string(),
        z.object({
          productSkus: z.string().min(1),
          category: z.string().min(1),
          subCategory: z.string().optional(),
          couponCode: z.string().optional(),
          device: z
            .object({
              type: z.string().optional(),
              sku: z.string().optional(),
            })
            .optional(),
        })
      ),
    })
    .optional()
    .default({ offerCode: {} }),
});

export type PurchaseJourneyContent = z.infer<typeof purchaseJourneyContentSchema>;

let activePurchaseJourneyContent: PurchaseJourneyContent | null = null;

type PurchaseJourneyContentLoadPhase = 'fetch' | 'validation';

export class PurchaseJourneyContentLoadError extends Error {
  public phase: PurchaseJourneyContentLoadPhase;
  public endpoint: string;

  constructor({
    cause,
    endpoint,
    phase,
  }: {
    cause: unknown;
    endpoint: string;
    phase: PurchaseJourneyContentLoadPhase;
  }) {
    const rootCauseMessage = cause instanceof Error ? cause.message : `${cause}`;

    super(`Failed to load purchase journey content during ${phase}: ${rootCauseMessage}`);
    this.name = 'PurchaseJourneyContentLoadError';
    this.phase = phase;
    this.endpoint = endpoint;
    this.cause = cause;
  }
}

export const parsePurchaseJourneyContent = (content: unknown): PurchaseJourneyContent =>
  purchaseJourneyContentSchema.parse(content);

export const getPurchaseJourneyContent = (): PurchaseJourneyContent => {
  if (!activePurchaseJourneyContent) {
    throw new Error('Purchase journey content has not been loaded');
  }

  return activePurchaseJourneyContent;
};

export const buildBroadbandSwitchOptions = (
  content: PurchaseJourneyContent = getPurchaseJourneyContent()
) => [
  {
    label: content.legacyConstants.switchButtons.broadband[0]?.label ?? "I'm new here",
    value: "I'm new here",
  },
  {
    label: content.legacyConstants.switchButtons.broadband[1]?.label ?? 'I have Sky TV',
    value: 'I have Sky TV',
  },
];

export const buildHomePhoneSwitchOptions = (
  content: PurchaseJourneyContent = getPurchaseJourneyContent()
) => [
  {
    label: content.legacyConstants.switchButtons.homePhone[0]?.label ?? 'Yes',
    value: 'Yes',
  },
  {
    label: content.legacyConstants.switchButtons.homePhone[1]?.label ?? 'No',
    value: 'No',
  },
];

export const buildAddressSwitchOptions = (
  content: PurchaseJourneyContent = getPurchaseJourneyContent()
) => [
  {
    label: content.legacyConstants.switchButtons.address[0]?.label ?? 'Owner',
    value: 'Owner',
  },
  {
    label: content.legacyConstants.switchButtons.address[1]?.label ?? 'Tenant',
    value: 'Tenant',
  },
];

export const buildHardwareDeliverySwitchOptions = (
  content: PurchaseJourneyContent = getPurchaseJourneyContent()
) => [
  {
    label: content.legacyConstants.switchButtons.hardwareDelivery[0]?.label ?? 'Yes',
    value: 'Yes',
  },
  {
    label: content.legacyConstants.switchButtons.hardwareDelivery[1]?.label ?? 'No',
    value: 'No',
  },
];

export const buildVulnerableSwitchOptions = (
  content: PurchaseJourneyContent = getPurchaseJourneyContent()
) => [
  {
    label: content.legacyConstants.switchButtons.vulnerable[0]?.label ?? 'Yes',
    value: 'Yes',
  },
  {
    label: content.legacyConstants.switchButtons.vulnerable[1]?.label ?? 'No',
    value: 'No',
  },
];

export const setPurchaseJourneyContent = (content: unknown): PurchaseJourneyContent => {
  activePurchaseJourneyContent = parsePurchaseJourneyContent(content);
  return activePurchaseJourneyContent;
};

const PurchaseJourneyContentContext = createContext<PurchaseJourneyContent | null>(null);

type PurchaseJourneyContentProviderProps = React.PropsWithChildren<{
  content?: unknown;
}>;

export const PurchaseJourneyContentProvider: React.FC<PurchaseJourneyContentProviderProps> = ({
  children,
  content,
}) => {
  const [value, setValue] = useState<PurchaseJourneyContent | null>(() =>
    content ? setPurchaseJourneyContent(content) : null
  );
  const [loadError, setLoadError] = useState<PurchaseJourneyContentLoadError | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadContent = async () => {
      setLoadError(null);

      try {
        let nextContent: unknown;

        try {
          nextContent = content ?? (await fetchPurchaseJourneyContent());
        } catch (error) {
          throw new PurchaseJourneyContentLoadError({
            cause: error,
            endpoint: getPurchaseJourneyContentEndpoint(),
            phase: 'fetch',
          });
        }

        let parsedContent: PurchaseJourneyContent;

        try {
          parsedContent = setPurchaseJourneyContent(nextContent);
        } catch (error) {
          throw new PurchaseJourneyContentLoadError({
            cause: error,
            endpoint: getPurchaseJourneyContentEndpoint(),
            phase: 'validation',
          });
        }

        if (isMounted) {
          setValue(parsedContent);
        }
      } catch (error) {
        if (isMounted) {
          setLoadError(
            error instanceof PurchaseJourneyContentLoadError
              ? error
              : new PurchaseJourneyContentLoadError({
                  cause: error,
                  endpoint: getPurchaseJourneyContentEndpoint(),
                  phase: 'fetch',
                })
          );
        }
      }
    };

    void loadContent();

    return () => {
      isMounted = false;
    };
  }, [content]);

  if (loadError) {
    throw loadError;
  }

  if (!value) {
    return null;
  }

  return (
    <PurchaseJourneyContentContext.Provider value={value}>
      {children}
    </PurchaseJourneyContentContext.Provider>
  );
};

export const usePurchaseJourneyContent = (): PurchaseJourneyContent => {
  const content = useContext(PurchaseJourneyContentContext);

  if (!content) {
    throw new Error('usePurchaseJourneyContent must be used within PurchaseJourneyContentProvider');
  }

  return content;
};
